import { isAxiosError } from 'axios';
import { APIS, URLS } from '@/shared/config/urls';
import { request } from '@/shared/utils/request';
import type {
  AdminOrderDetail,
  AdminOrderPage,
  AdminOrderParams,
  AdminOrderSummary,
  AdminPaidOrder,
  AdminPickupResult,
} from '../types/order.types';

export const fetchAdminOrderSummary = async (): Promise<AdminOrderSummary> => {
  return await request.get(`${URLS.BACK_DSH}${APIS.adminOrderSummary}`);
};

export const fetchAdminOrders = async (params: AdminOrderParams): Promise<AdminOrderPage> => {
  const query = new URLSearchParams();
  if (params.page) query.set('page', String(params.page));
  if (params.size) query.set('size', String(params.size));
  if (params.status) query.set('status', params.status);
  if (params.transfer_status) query.set('transfer_status', params.transfer_status);
  if (params.sort_by) query.set('sort_by', params.sort_by);
  if (params.order) query.set('order', params.order);
  if (params.q) query.set('q', params.q);
  return await request.get(`${URLS.BACK_DSH}${APIS.adminOrders(query.toString())}`);
};

export const fetchAdminOrder = async (orderId: string): Promise<AdminOrderDetail> => {
  return await request.get(`${URLS.BACK_DSH}${APIS.adminOrder(orderId)}`);
};

/** Batalkan. A paid order gets a refund recorded for staff to pay back by hand. Repeating is harmless. */
export const cancelAdminOrder = async (orderId: string, reason?: string) => {
  return await request.post(
    `${URLS.BACK_DSH}${APIS.adminOrderCancel(orderId)}`,
    reason ? { reason } : {}
  );
};

/** Request Jemput: creates the courier order when missing and asks the courier to pick the parcel up
 *  (a pre-order once its books are in, or another try). Repeating is harmless. */
export const requestAdminOrderPickup = async (orderId: string): Promise<AdminPickupResult> => {
  return await request.post(`${URLS.BACK_DSH}${APIS.adminOrderPickup(orderId)}`);
};

/** Konfirmasi Pembayaran: the transfer was found on the bank statement; the order is processed and a
 *  regular order is handed to the courier. Repeating is harmless. */
export const confirmAdminOrderPayment = async (orderId: string): Promise<AdminPaidOrder> => {
  return await request.post(`${URLS.BACK_DSH}${APIS.adminOrderConfirmPayment(orderId)}`);
};

/** Transfer Tidak Ditemukan: the customer sees the reason and can report again. Repeating is harmless. */
export const rejectAdminOrderTransfer = async ({
  orderId,
  reason,
}: {
  orderId: string;
  reason?: string;
}) => {
  return await request.post(
    `${URLS.BACK_DSH}${APIS.adminOrderRejectTransfer(orderId)}`,
    reason ? { reason } : {}
  );
};

/** Cetak Resi: the courier label as a PDF. The courier service makes it once; later calls get the same file. */
export const fetchAdminOrderLabel = async (orderId: string): Promise<Blob> => {
  try {
    return await request.get<Blob>(`${URLS.BACK_DSH}${APIS.adminOrderLabel(orderId)}`, {
      responseType: 'blob',
    });
  } catch (error) {
    // An error answer arrives as a Blob too: take the backend message out of it
    const data = isAxiosError(error) ? error.response?.data : undefined;
    if (error instanceof Error && data instanceof Blob) {
      try {
        const body: unknown = JSON.parse(await data.text());
        if (
          body &&
          typeof body === 'object' &&
          'message' in body &&
          typeof body.message === 'string'
        ) {
          error.message = body.message;
        }
      } catch {
        // not JSON: keep the generic message
      }
    }
    throw error;
  }
};

/** Live events (Server-Sent Events, session cookie); the browser reconnects after a dropped
 *  connection, not after a refusal (403). */
export const openAdminOrderEvents = () =>
  new EventSource(`${URLS.BACK_DSH}${APIS.adminOrderEvents}`, { withCredentials: true });
